import { useHistory } from "react-router-dom";
import CustomText from "../../../atomic/CustomText";
import Button from "../../../organism/Button";
import {
  SCC_ADDITIONAL_HISTORY_OPTIONS,
  SCC_ADDITIONAL_PATHOLOGY_OPTIONS,
  SCC_AGGRESSIVE_SUBTYPE_FOOTNOTE,
  SCC_REQUIRED_FIELD_LABELS,
  SCC_REQUIRED_HISTORY_QUESTIONS,
  SCC_REQUIRED_PATHOLOGY_QUESTIONS,
} from "../../../../constants/sccClinicalInfoConfig";
import {
  HistoryPhysicalExamClinicalInfo,
  PathologySurgicalFindingsClinicalInfo,
} from "../../../../services/models/sccClinicalSection.model";
import RadioOptions from "../../../../services/models/radioOptions.model";

function formatCheckboxSelections(
  values: string[],
  options: RadioOptions[]
): string {
  if (!values?.length) return "---";
  return values
    .map((value) => {
      const option = options.find((item) => item.value === value);
      return (
        option?.label?.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() ??
        String(value)
      );
    })
    .join(", ");
}

interface SCCClinicalInformationReviewProps {
  values: {
    historyPhysicalExam?: HistoryPhysicalExamClinicalInfo;
    pathologySurgicalFindings?: PathologySurgicalFindingsClinicalInfo;
  };
  approval?: boolean;
  approvalSectionUrl?: string;
  mainHeading?: string;
}

function SCCClinicalInformationReview({
  values,
  approval = false,
  approvalSectionUrl,
  mainHeading = "Clinical Information",
}: Readonly<SCCClinicalInformationReviewProps>): JSX.Element {
  const history = useHistory();
  const historyPhysicalExam = values?.historyPhysicalExam;
  const pathologySurgicalFindings = values?.pathologySurgicalFindings;

  const renderRequiredAnswer = (fieldKey: string, answer: string) => (
    <div key={fieldKey} className="mb-3">
      <CustomText
        text={SCC_REQUIRED_FIELD_LABELS[fieldKey]}
        fontWeight="font-semibold"
        className="text-sm text-royalBlue"
      />
      <CustomText
        text={answer || "---"}
        fontWeight="font-normal"
        className="text-sm mt-1"
      />
    </div>
  );

  const content = (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <CustomText
          text="REQUIRED SECTION"
          fontWeight="font-bold"
          className="text-sm uppercase mb-4"
        />
        <CustomText
          text="History and Physical Exam:"
          fontWeight="font-semibold"
          className="mb-2"
        />
        {SCC_REQUIRED_HISTORY_QUESTIONS.map((question) => {
          const fieldKey = question.name.split(".").pop() ?? "";
          return renderRequiredAnswer(
            fieldKey,
            (historyPhysicalExam?.[
              fieldKey as keyof HistoryPhysicalExamClinicalInfo
            ] as string) ?? ""
          );
        })}
        <CustomText
          text="Surgical and Pathological Findings:"
          fontWeight="font-semibold"
          className="mb-2 mt-4"
        />
        {SCC_REQUIRED_PATHOLOGY_QUESTIONS.map((question) => {
          const fieldKey = question.name.split(".").pop() ?? "";
          return renderRequiredAnswer(
            fieldKey,
            (pathologySurgicalFindings?.[
              fieldKey as keyof PathologySurgicalFindingsClinicalInfo
            ] as string) ?? ""
          );
        })}
      </div>

      <div>
        <CustomText
          text="ADDITIONAL HIGH RISK FEATURES"
          fontWeight="font-bold"
          className="text-sm uppercase mb-4"
        />
        <CustomText
          text="History and Physical Exam:"
          fontWeight="font-semibold"
          className="mb-2"
        />
        <CustomText
          text={formatCheckboxSelections(
            historyPhysicalExam?.additionalHistoryPhysicalExam ?? [],
            SCC_ADDITIONAL_HISTORY_OPTIONS
          )}
          fontWeight="font-normal"
          className="text-sm mb-4"
        />
        <CustomText
          text="Surgical and Pathological Findings:"
          fontWeight="font-semibold"
          className="mb-2"
        />
        <CustomText
          text={formatCheckboxSelections(
            pathologySurgicalFindings?.additionalPathologySurgicalFindings ??
              [],
            SCC_ADDITIONAL_PATHOLOGY_OPTIONS
          )}
          fontWeight="font-normal"
          className="text-sm"
        />
        <CustomText
          text={SCC_AGGRESSIVE_SUBTYPE_FOOTNOTE}
          fontWeight="font-normal"
          className="text-xs text-darkGray mt-3"
        />
      </div>
    </div>
  );

  if (approval) {
    return (
      <div className="w-full">
        <div className="flex justify-between items-center">
          <CustomText
            text={mainHeading}
            fontWeight="font-semibold"
            className="my-2 mt-5"
          />
          {approvalSectionUrl && (
            <Button
              textInformation={{
                text: "edit",
                fontWeight: "font-semibold",
              }}
              loading={false}
              disabled={false}
              onClick={() => history.push(approvalSectionUrl)}
              buttonTheme="secondary"
              className="mt-3 bg-white"
            />
          )}
        </div>
        {content}
      </div>
    );
  }

  return (
    <div className="bg-sideNavGray px-6 py-4 border border-roundedInputBorder rounded">
      {content}
    </div>
  );
}

export default SCCClinicalInformationReview;
